import { PriceStrategy } from '@snap-types/models/enums';

import { recalculateAlpha } from '../strategies/alpha';
import { recalculateCAPPriceSegmentation } from '../strategies/cap-price-segmentation';
import { recalculateFixedCustomer } from '../strategies/fixed-customer';
import { recalculateFixedDefault } from '../strategies/fixed-default';
import { recalculateFixedDiscount } from '../strategies/fixed-discount';
import { recalculateFixedMarket } from '../strategies/fixed-market';
import { recalculateNewPart } from '../strategies/new-part';
import { recalculateNewPartUserCost } from '../strategies/new-part-user-cost';
import { recalculateNewPartUserPrice } from '../strategies/new-part-user-price';
import { recalculatePriceSegmentation } from '../strategies/price-segmentation';
import { recalculatePriceSegmentationWithTargetGrossMargin } from '../strategies/price-segmentation-with-target-gross-margin';
import { recalculateRAFFixedMarket } from '../strategies/raf-fixed-market';
import { recalculateRAFTargetGrossMargin } from '../strategies/raf-target-gross-margin';
import { recalculateTargetContributionMargin } from '../strategies/target-contribution-margin';
import { recalculateTargetGrossMargin } from '../strategies/target-gross-margin';
import { recalculateUserImport } from '../strategies/user-import';
import { recalculateUserPrice } from '../strategies/user-price';
import { RecalculatePriceStrategy } from '../types';

type Calculation = keyof RecalculatePriceStrategy;

/**
 * Builds a record of price strategies mapped to their calculation functions
 * for the specified calculation type.
 */
export function buildStrategyCalculations<T extends Calculation>(
  calculation: T,
): Record<PriceStrategy, RecalculatePriceStrategy[T]> {
  return {
    [PriceStrategy.FixedCustomer]: recalculateFixedCustomer[calculation],
    [PriceStrategy.FixedDefault]: recalculateFixedDefault[calculation],
    [PriceStrategy.TargetGrossMargin]:
      recalculateTargetGrossMargin[calculation],
    [PriceStrategy.FixedDiscount]: recalculateFixedDiscount[calculation],
    [PriceStrategy.TargetContributionMargin]:
      recalculateTargetContributionMargin[calculation],
    [PriceStrategy.FixedMarket]: recalculateFixedMarket[calculation],
    [PriceStrategy.PriceSegmentation]:
      recalculatePriceSegmentation[calculation],
    [PriceStrategy.PriceSegmentationWithTargetGrossMargin]:
      recalculatePriceSegmentationWithTargetGrossMargin[calculation],
    [PriceStrategy.CAPPriceSegmentation]:
      recalculateCAPPriceSegmentation[calculation],
    [PriceStrategy.UserPrice]: recalculateUserPrice[calculation],

    // @NOTE(mrs): Service Charge is basically just User Price, so we are
    // using that price relcaculate functionality
    [PriceStrategy.ServiceCharge]: recalculateUserPrice[calculation],
    [PriceStrategy.NewPartUserPrice]: recalculateNewPartUserPrice[calculation],
    [PriceStrategy.UserImport]: recalculateUserImport[calculation],
    [PriceStrategy.RAFFixedMarket]: recalculateRAFFixedMarket[calculation],
    [PriceStrategy.RAFTargetGrossMargin]:
      recalculateRAFTargetGrossMargin[calculation],
    [PriceStrategy.NewPart]: recalculateNewPart[calculation],
    [PriceStrategy.NewPartUserCost]: recalculateNewPartUserCost[calculation],
    [PriceStrategy.Alpha]: recalculateAlpha[calculation],
  };
}
